import { Injectable } from '@angular/core';

export interface FilterState {
  [key: string]: any;
  timestamp?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LocalStorageFilterService {
  private readonly MAX_SEARCH_HISTORY = 10;

  /**
   * Save search term to history
   */
  saveSearchToHistory(key: string, searchTerm: string): void {
    if (!searchTerm || searchTerm.trim() === '') {
      return;
    }

    const trimmed = searchTerm.trim();
    const history = this.getSearchHistory(key);
    
    // Remove if already exists
    const filtered = history.filter(term => term !== trimmed);
    
    // Add to beginning
    filtered.unshift(trimmed);
    
    // Keep only last N items
    const limited = filtered.slice(0, this.MAX_SEARCH_HISTORY);
    
    try {
      localStorage.setItem(key, JSON.stringify(limited));
    } catch (error) {
      console.error(`Error saving search history for ${key}:`, error);
    }
  }

  /**
   * Get search history
   */
  getSearchHistory(key: string): string[] {
    try {
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error(`Error loading search history for ${key}:`, error);
      return [];
    }
  }

  /**
   * Clear search history
   */
  clearSearchHistory(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Error clearing search history for ${key}:`, error);
    }
  }

  /**
   * Save filter state
   */
  saveFilters(key: string, filters: FilterState): void {
    try {
      filters.timestamp = new Date().toISOString();
      localStorage.setItem(key, JSON.stringify(filters));
    } catch (error) {
      console.error(`Error saving filters for ${key}:`, error);
    }
  }

  /**
   * Load filter state
   */
  loadFilters(key: string): FilterState | null {
    try {
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error(`Error loading filters for ${key}:`, error);
      return null;
    }
  }

  /**
   * Save pagination state
   */
  savePagination(key: string, currentPage: number, pageSize: number): void {
    try {
      const pagination = {
        currentPage,
        pageSize,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(key, JSON.stringify(pagination));
    } catch (error) {
      console.error(`Error saving pagination for ${key}:`, error);
    }
  }

  /**
   * Load pagination state
   */
  loadPagination(key: string): { currentPage: number; pageSize: number } | null {
    try {
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error(`Error loading pagination for ${key}:`, error);
      return null;
    }
  }

  /**
   * Clear all stored data for a page
   */
  clearPageData(searchKey: string, filterKey: string, paginationKey: string): void {
    localStorage.removeItem(searchKey);
    localStorage.removeItem(filterKey);
    localStorage.removeItem(paginationKey);
  }
}

